import React from 'react';
import { Box, Button, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u7_x4_netzteile';
const IMAGE_BASE = '/assets/drawable/';
const IMAGE_KEYS = ['img_0'];
const IMAGE_KEYS_DYNAMIC = ['img_0'];

interface StromversorgungInfo {
  name: string;
  typ: string;
  titel: string;
  allgemeineBeschreibung: string;
  eingangsspannungSpez: string;
  ausgangsspannungSpez: string;
  typischeLeistungVAoderWatt: string;
  anschlussPrinzipPrimär: string;
  anschlussPrinzipSekundär: string;
  leistungsberechnungHinweise: string;
  wichtigeHinweiseUndAuswahl: string;
  beispielProdukt?: string;
}

const netzteilVarianten: StromversorgungInfo[] = [
  {
    name: 'Schaltnetzteil 24V DC (230V AC → 24V DC)',
    typ: 'Netzteil (DC)',
    titel: 'Schaltnetzteil 24V DC',
    allgemeineBeschreibung: 'Weit verbreitetes DC-Netzteil zur Versorgung von SPS, Sensorik/Aktorik.',
    eingangsspannungSpez: '1~ 230 V AC',
    ausgangsspannungSpez: '24 V DC (z. B. 5 A)',
    typischeLeistungVAoderWatt: 'z. B. 120 W',
    anschlussPrinzipPrimär: 'L, N, PE – ggf. Netzeingangsfilter, Vorsicherung beachten.',
    anschlussPrinzipSekundär: '+V, 0V – ggf. Parallelklemmen, Erdungskonzept beachten.',
    leistungsberechnungHinweise: 'P = U × I; Einschaltstrom, Wirkungsgrad, Derating über Temperatur berücksichtigen.',
    wichtigeHinweiseUndAuswahl: 'Primär/sekundär absichern, Belüftung, Rückspeisefestigkeit, Parallelschaltung nur bei Freigabe.',
    beispielProdukt: 'ABB CP-T 24/5.0 (Beispiel)'
  },
  {
    name: 'Drehstrom-Netzteil 24V DC (3×400V AC → 24V DC)',
    typ: 'Netzteil (DC)',
    titel: 'Dreiphasiges Netzteil 24V DC',
    allgemeineBeschreibung: 'Für höhere Leistungen und bessere Netzauslastung in Industrieanlagen.',
    eingangsspannungSpez: '3~ 400 V AC',
    ausgangsspannungSpez: '24 V DC (z. B. 10–20 A)',
    typischeLeistungVAoderWatt: 'z. B. 480 W',
    anschlussPrinzipPrimär: 'L1, L2, L3, PE – Vorsicherung je Phase, symmetrische Belastung.',
    anschlussPrinzipSekundär: '+V, 0V; ggf. mehrere Abgänge, Sammelschiene 24 V.',
    leistungsberechnungHinweise: 'Leistungssumme aller Verbraucher, Spitzenströme, Kurzzeitüberlastfähigkeit.',
    wichtigeHinweiseUndAuswahl: 'EMV, Umgebungstemp., Redundanzmodule möglich, Überwachung (DC OK).'
  },
  {
    name: 'Sicherheitstrafo 230V → 24V AC',
    typ: 'Transformator (AC)',
    titel: 'Trenn-/Sicherheitstrafo 24V AC',
    allgemeineBeschreibung: 'Klassischer Trafo für 24 V AC Anwendungen (z. B. Ventile).',
    eingangsspannungSpez: '1~ 230 V AC',
    ausgangsspannungSpez: '24 V AC (z. B. 100 VA)',
    typischeLeistungVAoderWatt: 'z. B. 100 VA',
    anschlussPrinzipPrimär: 'Primär L/N/PE; ggf. Anzapfungen für Feineinstellung.',
    anschlussPrinzipSekundär: 'Sekundär ~24 V; ggf. Mittelanzapfung; Schutz durch sekundäre Sicherung.',
    leistungsberechnungHinweise: 'S = U × I (VA); Spannungsabfall bei Last; Einschaltstrom gering.',
    wichtigeHinweiseUndAuswahl: 'Thermoschutz, Montage, Kurzschlussschutz sekundär.'
  },
  {
    name: 'Trafo 3×400V → 230V AC',
    typ: 'Transformator (AC)',
    titel: 'Drehstrom → Einphasen-Trafo 230 V',
    allgemeineBeschreibung: 'Versorgung von 230 V AC Verbrauchern aus 400 V Netzen.',
    eingangsspannungSpez: '3~ 400 V AC (Primär)',
    ausgangsspannungSpez: '1~ 230 V AC (Sekundär)',
    typischeLeistungVAoderWatt: 'abhängig von Baugröße (z. B. 500 VA)',
    anschlussPrinzipPrimär: 'Dreieck/Stern je nach Ausführung; Schutzmaßnahme primär.',
    anschlussPrinzipSekundär: '230 V AC L/N; Schutzerdung; Absicherung.',
    leistungsberechnungHinweise: 'S = U × I; Spannungsfall; Verlustleistung.',
    wichtigeHinweiseUndAuswahl: 'Kurzschlussfestigkeit, Erwärmung, Montage.'
  }
];

const introBlocks = [
  {
    heading: 'Stromversorgungen: Netzteile und Trafos',
    body: 'Stromversorgungen stellen die für Steuerungen und Aktoren benötigten Spannungen bereit. DC-Netzteile (z. B. 24 V) und AC-Transformatoren (z. B. 24 V AC) decken unterschiedliche Einsatzzwecke ab.'
  },
  {
    heading: 'Auslegung und Sicherheit',
    body: 'Leistungsbedarf aller Verbraucher summieren, Reserven einplanen, Wirkungsgrad/Derating beachten. Absicherung primär/sekundär, EMV und Umgebungstemperaturen berücksichtigen.'
  }
];

const M6_U7_X4_Netzteile: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  };

  const getDynamicImageSrc = (selectedType: string, idx: number, fallback: string) => {
    const key = `dyn_${selectedType}_img_${idx}`;
    return getImageSrc(key, fallback);
  };

  const [selectedType, setSelectedType] = React.useState<string>(netzteilVarianten[0].name);
  const currentInfo = netzteilVarianten.find(n => n.name === selectedType) || netzteilVarianten[0];

  const mergedIntroBlocks = React.useMemo(
    () => introBlocks.map((tb, i) => ({
      ...tb,
      body: override?.texts?.[`intro_${i}`] || tb.body
    })),
    [override]
  );

  const mergedDynamicBlocks = React.useMemo(() => [
    { heading: currentInfo.titel, body: override?.texts?.[`dyn_${selectedType}_0`] || currentInfo.allgemeineBeschreibung },
    { heading: 'Eingang', body: override?.texts?.[`dyn_${selectedType}_1`] || currentInfo.eingangsspannungSpez },
    { heading: 'Ausgang', body: override?.texts?.[`dyn_${selectedType}_2`] || currentInfo.ausgangsspannungSpez },
    { heading: 'Leistung', body: override?.texts?.[`dyn_${selectedType}_3`] || currentInfo.typischeLeistungVAoderWatt },
    { heading: 'Anschluss (Primär)', body: override?.texts?.[`dyn_${selectedType}_4`] || currentInfo.anschlussPrinzipPrimär },
    { heading: 'Anschluss (Sekundär)', body: override?.texts?.[`dyn_${selectedType}_5`] || currentInfo.anschlussPrinzipSekundär },
    { heading: 'Leistungsberechnung', body: override?.texts?.[`dyn_${selectedType}_6`] || currentInfo.leistungsberechnungHinweise },
    { heading: 'Wichtige Hinweise', body: (override?.texts?.[`dyn_${selectedType}_7`] || currentInfo.wichtigeHinweiseUndAuswahl) + (currentInfo.beispielProdukt ? `\nBeispiel: ${currentInfo.beispielProdukt}` : '') }
  ], [selectedType, override, currentInfo]);

  const allTextBlocks = React.useMemo(
    () => [...mergedIntroBlocks, ...mergedDynamicBlocks],
    [mergedIntroBlocks, mergedDynamicBlocks]
  );

  const fullPlainText = React.useMemo(() =>
    allTextBlocks.map(b => `${b.heading}. ${b.body}`).join(' '),
    [allTextBlocks]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Bild-Upload:', error);
      alert('Fehler beim Hochladen des Bildes.');
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Text-Speichern:', error);
      alert('Fehler beim Speichern des Textes.');
    }
  }, [token, refresh]);

  const handleTypeChange = (event: any) => {
    setSelectedType(String(event.target.value));
  };

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.7.4 Netzteile & Trafos', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGE_KEYS.map((key) =>
          React.createElement(ZoomableImage, {
            key: key,
            src: getImageSrc(key, `${IMAGE_BASE}zzz_help.png`),
            alt: 'Netzteil/Trafo',
            label: 'Bild Stromversorgung',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(key, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(EditableTextBlock, {
          key: 0,
          pageId: PAGE_ID,
          blockId: 'intro_0',
          heading: mergedIntroBlocks[0].heading,
          body: mergedIntroBlocks[0].body,
          onSave: (newBody: string) => handleTextSave('intro_0', newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: 1,
          pageId: PAGE_ID,
          blockId: 'intro_1',
          heading: mergedIntroBlocks[1].heading,
          body: mergedIntroBlocks[1].body,
          onSave: (newBody: string) => handleTextSave('intro_1', newBody)
        }),
        React.createElement(FormControl, { fullWidth: true, sx: { mt: 2, mb: 2 } },
          React.createElement(InputLabel, { id: 'netzteil-select-label' }, 'Variante auswählen'),
          React.createElement(Select, {
            labelId: 'netzteil-select-label',
            id: 'netzteil-select',
            value: selectedType,
            label: 'Variante auswählen',
            onChange: handleTypeChange
          },
            ...netzteilVarianten.map((v) =>
              React.createElement(MenuItem, { key: v.name, value: v.name }, v.name)
            )
          )
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          IMAGE_KEYS_DYNAMIC.map((key) =>
            React.createElement(ZoomableImage, {
              key: key,
              src: getDynamicImageSrc(selectedType, 0, `${IMAGE_BASE}zzz_help.png`),
              alt: 'Bild für Spinner-Typ',
              label: 'Spinner-Bild',
              onReplace: canUploadImages ? (file: File) => handleImageReplace(`dyn_${selectedType}_img_0`, file) : undefined
            })
          )
        ),
        React.createElement(EditableTextBlock, { key: `${selectedType}_0`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_0`, heading: mergedDynamicBlocks[0]?.heading || 'Allgemeine Beschreibung', body: mergedDynamicBlocks[0]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_0`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_1`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_1`, heading: 'Eingang', body: mergedDynamicBlocks[1]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_1`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_2`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_2`, heading: 'Ausgang', body: mergedDynamicBlocks[2]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_2`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_3`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_3`, heading: 'Leistung', body: mergedDynamicBlocks[3]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_3`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_4`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_4`, heading: 'Anschluss (Primär)', body: mergedDynamicBlocks[4]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_4`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_5`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_5`, heading: 'Anschluss (Sekundär)', body: mergedDynamicBlocks[5]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_5`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_6`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_6`, heading: 'Leistungsberechnung', body: mergedDynamicBlocks[6]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_6`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedType}_7`, pageId: PAGE_ID, blockId: `dyn_${selectedType}_7`, heading: 'Wichtige Hinweise', body: mergedDynamicBlocks[7]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_7`, newBody) }),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u7_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M6_U7_X4_Netzteile;


